import { zonedDayBounds, type CalendarDate, type IanaTimeZone } from '@baby-tracker/validation';
import type { ReactElement } from 'react';

import { sleepRhythm, type SleepSpan } from '../../../lib/sleep-rhythm.js';
import { errorText } from '../../ui/error-text.js';
import { FormError } from '../../ui/FormError.js';
import styles from './tracking.module.css';

export function SleepRhythm({
    date,
    timeZone,
    now,
    sleeps,
    pending,
    error,
}: {
    date: CalendarDate;
    timeZone: IanaTimeZone;
    now: number;
    sleeps: readonly SleepSpan[];
    pending: boolean;
    error: Error | null;
}): ReactElement | null {
    if (error) {
        return <FormError message={errorText(error)} />;
    }
    if (pending) {
        return null;
    }
    const bounds = zonedDayBounds(date, timeZone);
    const rhythm = sleepRhythm(sleeps, Date.parse(bounds.from), Date.parse(bounds.to), now);
    const showNowLabel = rhythm.now !== null && rhythm.now >= 0.15 && rhythm.now <= 0.85;
    return (
        <div className={styles.rhythm}>
            <p className={styles.rhythmLabel}>{rhythm.label}</p>
            <div className={styles.rhythmFigure} role="img" aria-label={rhythm.description}>
                <div
                    className={
                        rhythm.blocks.length === 0 ? styles.rhythmTrackEmpty : styles.rhythmTrack
                    }
                >
                    {rhythm.blocks.map((block) => (
                        <span
                            key={`${block.start}-${block.end}`}
                            className={
                                block.running ? styles.rhythmSleepRunning : styles.rhythmSleep
                            }
                            style={{
                                insetInlineStart: `${block.start * 100}%`,
                                inlineSize: `${(block.end - block.start) * 100}%`,
                            }}
                        />
                    ))}
                    {rhythm.now !== null ? (
                        <span
                            className={styles.rhythmNow}
                            style={{ insetInlineStart: `${rhythm.now * 100}%` }}
                        />
                    ) : null}
                </div>
                <div className={styles.rhythmScale}>
                    <span>{formatClock(Date.parse(bounds.from), timeZone)}</span>
                    {showNowLabel && rhythm.now !== null ? (
                        <span
                            className={styles.rhythmNowLabel}
                            style={{ insetInlineStart: `${rhythm.now * 100}%` }}
                        >
                            {formatClock(now, timeZone)}
                        </span>
                    ) : null}
                    <span>{formatClock(Date.parse(bounds.to), timeZone)}</span>
                </div>
            </div>
        </div>
    );
}

function formatClock(instant: number, timeZone: string): string {
    return new Intl.DateTimeFormat(undefined, {
        timeZone,
        hour: 'numeric',
        minute: '2-digit',
    }).format(instant);
}
